/**
 * Admin-side audio controller.
 *
 * Each TrackCard in the /audio list owns its own wavesurfer instance
 * (lazy-initialised on first play). Without a coordinator, clicking
 * play on track A, then on track B, would start both — overlapping
 * audio. The bus solves that with a single "currently playing" slot:
 *
 *   - When a tile starts to play, it calls `audioBus.play(myId)`.
 *     The bus fires a window `audiobus:stop` event for any previous
 *     owner, which pauses its wavesurfer. The new tile then proceeds
 *     to call `ws.play()`.
 *   - When a tile finishes / is destroyed, it calls `audioBus.stop(myId)`
 *     to clear the slot.
 *   - When the user clicks Edit or Delete on a playing card, the
 *     handler also calls `audioBus.stop(myId)` before invoking the
 *     parent's callback — guaranteeing no audio leaks into the
 *     modal.
 *
 * Mirrors the design of the web frontend's
 * `merryflix-frontend/src/lib/audio-bus.ts` so the UX feels identical
 * between the admin list view and the wizard's step 4.
 */

type Listener = (current: string | null) => void;

let current: string | null = null;
const listeners = new Set<Listener>();

/** Custom DOM event used to tell the previous owner to stop. We use a
 *  DOM event (instead of a bus method) so the previous tile's React
 *  component can subscribe via a window event listener regardless of
 *  React tree position. */
const STOP_EVENT = 'audiobus:stop';

function emit() {
  for (const l of listeners) l(current);
}

export const audioBus = {
  /** Subscribe to "currently playing" changes. Returns unsubscribe. */
  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    // Push the current value immediately so the listener doesn't have
    // to call audioBus.current() separately to render.
    fn(current);
    return () => { listeners.delete(fn); };
  },

  current(): string | null {
    return current;
  },

  /**
   * Claim the "currently playing" slot. If another tile was already
   * playing, fire a stop event for it first so it can pause its
   * wavesurfer. The caller is expected to actually call `ws.play()`
   * separately — the bus only manages the slot + tells others to stop.
   */
  play(trackId: string): void {
    if (current && current !== trackId) {
      window.dispatchEvent(new CustomEvent(STOP_EVENT, { detail: { id: current } }));
    }
    current = trackId;
    emit();
  },

  /** Clear the slot if we own it. Called by a tile when it finishes,
   *  pauses, or the user hits Stop on the MiniPlayer. */
  stop(trackId: string): void {
    if (current === trackId) {
      current = null;
      emit();
    }
  },

  /** Subscribe to "another tile started, please stop yourself" events.
   *  Returns an unsubscribe. The handler receives the id of the tile
   *  that should pause — match against your own id to ignore. */
  onStopRequest(fn: (id: string) => void): () => void {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ id: string }>;
      fn(ce.detail.id);
    };
    window.addEventListener(STOP_EVENT, handler);
    return () => window.removeEventListener(STOP_EVENT, handler);
  },
};
